























































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { mapState, mapGetters } from "vuex";
import SortableCategory from "./SortableCategory.vue";
import _ from "@/boot/lodash";

// Types
import { IState } from "@/store";
import {
  ISortableOptions,
  ISortableData,
  ISortableCategory
} from "@/models/reordering";
import { SortableItems } from "@/data/enums/sortableItems";
import { IBrand } from "@/models/brands";

@Component({
  components: {
    "sortable-category": SortableCategory
  },
  computed: {
    ...mapState({
      brandId(state: IState) {
        return state.brand.id;
      },
      sortableCategories(state: IState, getters) {
        return getters[`${this.sortableCategoriesModule}/listArray`]({
          scope: this.sortableCategoriesScope
        });
      },

      sortableCategoriesScope(state: IState, getters) {
        return getters[`${this.sortableCategoriesModule}/scope`](
          this.brandId,
          this.type,
          `${this.scope}_${this._uid}`
        );
      }
    }),
    ...mapGetters({
      getCategoryItems: "sortableCategories/getCategoryItems"
    })
  }
})
export default class MultilevelSortableList extends Vue {
  // Getters
  public brandId!: IBrand["id"];
  public sortableCategoriesScope!: string;
  public sortableCategories!: ISortableCategory[];
  public getCategoryItems!: Function;

  // Props
  @Prop({ required: true })
  public initialData!: ISortableData[];

  @Prop({ required: true })
  public sortableItemName!: string;

  @Prop({ required: true })
  public itemName!: string;

  @Prop({ required: true })
  public type!: string;

  @Prop({ default: "" })
  public scope!: string;

  @Prop({ default: 10 })
  public limit!: number;

  @Prop({ default: false })
  public isProcessing!: boolean;

  @Prop({ default: false })
  public lockToParent!: boolean;

  @Prop({ default: true })
  public isItemClickable!: boolean;

  // Optional functionalities
  @Prop({ default: () => null })
  public loadItems!: Function;

  @Prop({ default: () => null })
  public reorder!: Function;

  @Prop({ default: () => null })
  public createSubcategory!: Function;

  @Prop({ default: () => null })
  public createItem!: Function;

  @Prop({ default: () => null })
  public confirmDelete!: Function;

  @Prop({ default: () => null })
  public detach!: Function;

  @Prop({ default: () => null })
  public view!: Function;

  @Prop({ default: () => null })
  public duplicateItem!: Function;

  @Prop({ default: false })
  public isSelectable!: boolean;

  @Prop({ default: false })
  public isCategorySelectable!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: true })
  public multipleSelect!: boolean;

  @Prop({ default: true })
  public showCategoryItems!: boolean;

  @Prop({ default: "" })
  public routeName!: string;

  @Prop({ default: true })
  public showCounters!: boolean;

  // Variables
  public savedSortableCategoriesScope = "";
  public sortElems: ISortableData[] = [];
  public savedOrder: ISortableData[] = [];

  public sortableCategoriesModule = "sortableCategories";

  public isReordering = false;
  public sortableCategoriesId = this.$uuid();

  public sortableOptions: ISortableOptions = {
    chosenClass: "dragged-item",
    handle: ".drag-and-drop",
    group: {
      name: "sortable-list-2",
      pull: true,
      put: true
    },
    animation: 250,
    fallbackOnBody: true
  };

  public async created() {
    this.savedSortableCategoriesScope = this.sortableCategoriesScope;
    this.setSortableCategories(this.initialData);
  }

  public setSortableCategories(data) {
    this.$store.commit(`${this.sortableCategoriesModule}/setInitialState`, {
      scope: this.sortableCategoriesScope,
      total: data.length,
      data
    });
  }

  public clearSortableSelections(selectionId) {
    this.$store.commit(`${this.sortableCategoriesModule}/clearAllSelections`, {
      scope: this.sortableCategoriesScope,
      objectId: selectionId
    });
  }

  public async onDragDrop(movedItemData) {
    this.isReordering = true;
    this.savedOrder = _.cloneDeep(this.sortElems);

    try {
      const result = await this.reorder(movedItemData);

      if (
        !_.get(result, "ignoreRefresh") &&
        _.get(movedItemData, "itemMoved.type") === SortableItems.CATEGORY
      ) {
        await this.deleteCategory(movedItemData.itemMoved.id);
        await this.pasteCategory(movedItemData.newCategory, {
          category: movedItemData.itemMoved,
          index: movedItemData.newIndex
        });
      }

      this.$store.dispatch("toast/show", {
        message: this.$t("_.order_updated")
      });
    } catch (error) {
      this.$store.dispatch("toast/error", {
        message: _.get(error, "response.data.error.data.parent_id[0]", "")
      });

      this.refreshList();
    }

    this.isReordering = false;
  }

  public async selectElement(item, isSelected) {
    // If item or category can be selectable
    if (item.type === SortableItems.ITEM || this.isCategorySelectable) {
      if (!this.multipleSelect) this.clearSortableSelections(item.id);
      this.$emit("selectElement", item, isSelected);
    }

    // If category selection means select all products
    else {
      if (!this.loadItems) return; // check

      this.isReordering = true;

      let categoryItems = item.items;

      if (!item.isFullyLoaded) {
        const { items, total } = await this.loadItems(
          item.id,
          item.offset,
          item.itemsTotal
        );
        this.$store.commit(
          `${this.sortableCategoriesModule}/updateCategoryItems`,
          {
            scope: this.sortableCategoriesScope,
            categoryId: item.id,
            data: {
              items: _.map(items, loadedItem => {
                return {
                  ...loadedItem,
                  ...{ isSelected: loadedItem.isDisabled ? false : true }
                };
              }),
              total
            }
          }
        );

        categoryItems = _.concat(categoryItems, items);
      }

      categoryItems = _.map(
        this.getCategoryItems({
          scope: this.sortableCategoriesScope,
          categoryId: item.id
        }),
        categoryItem => {
          return {
            ...categoryItem,
            ...(categoryItem.type === SortableItems.ITEM &&
            !categoryItem.isDisabled
              ? { isSelected }
              : {})
          };
        }
      );

      _.forEach(
        _.filter(categoryItems, categoryItem => {
          return (
            categoryItem.type === SortableItems.ITEM && !categoryItem.isDisabled
          );
        }),
        item => {
          this.$emit("selectElement", item, isSelected);
        }
      );

      this.$store.commit(`${this.sortableCategoriesModule}/updateCategory`, {
        scope: this.sortableCategoriesScope,
        categoryId: item.id,
        data: {
          isOpened: true,
          items: categoryItems
        }
      });

      this.isReordering = false;
    }
  }

  public async openCloseCategory(categoryId, isOpen, offset = 0) {
    this.$emit("openCloseCategory", categoryId, isOpen, offset);
  }

  public deleteCategory(categoryId) {
    return this.$store.commit(
      `${this.sortableCategoriesModule}/deleteCategory`,
      {
        scope: this.sortableCategoriesScope,
        categoryId
      }
    );
  }

  public pasteCategory(categoryId, data) {
    return this.$store.commit(
      `${this.sortableCategoriesModule}/pasteCategory`,
      {
        scope: this.sortableCategoriesScope,
        categoryId: categoryId,
        data
      }
    );
  }

  public refreshList() {
    this.sortableCategoriesId = this.$uuid();
  }

  // Watch
  @Watch("sortableCategories", { deep: true })
  public sortableCategoriesChanged() {
    this.sortElems = _.cloneDeep(this.sortableCategories);
    this.savedOrder = _.cloneDeep(this.sortElems);
  }

  public beforeDestroy() {
    this.$store.dispatch(`${this.sortableCategoriesModule}/binList`, {
      scope: this.savedSortableCategoriesScope
    });
  }
}
